import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import moment from "moment";
import { Button, message } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { POST_CUTTING_PROCESS_LIST } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ListTable from "common/ListTable";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { CUTTING_CREATE_PERMISSION } from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";
import style from "../production.module.css";

const PostCuttingProcess = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchError, setFetchError] = useState(null);

  // Others
  const navigate = useNavigate();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllPostCuttingProcess = useCallback(
    async (filterValues) => {
      console.log('filterValues', filterValues);

      setFetchError(null);
      setLoading(true);

      const query = `${POST_CUTTING_PROCESS_LIST}?page=${currentPage}`;
      const bodyData = {
        ...filterValues,
      };

      const response = await getData(query, false, bodyData);
      if (response && response?.data?.code === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.data?.per_page,
          current: response?.data?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.data?.current_page,
          total: response?.data?.data?.data?.total,
          summaryReceive: response?.data?.data?.summary_receive ? Number(response?.data?.data?.summary_receive) : 0,
          summarySend: response?.data?.data?.summary_send ? Number(response?.data?.data?.summary_send) : 0,
        });
        setTableData(response?.data?.data?.data?.data);
      } else {
        setFetchError("Could not load this list.");
        setLoading(false);
        message.error("Error getting post cutting data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    const existingFilter =
      JSON.parse(localStorage.getItem("post_cutting_filter_filter_value")) || {};

    console.log('existingFilter', existingFilter);
    

    getAllPostCuttingProcess(existingFilter);
  }, [refresh, getAllPostCuttingProcess]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const gotoAddRoute = () => {
    navigate("/production/post-cutting-process/entry");
  };

  const gotoUpdateRoute = (record) => {
    const sales_contract_id = record?.sales_contract_id;
    const style_id = record?.styles?.id;
    const sub_contract_factory_id = record?.sub_contract_factory_id;
    const buying_office_id = record?.buying_office_id;
    const movement_date = record?.movement_date
      ? moment(record?.movement_date).format("YYYY-MM-DD")
      : null;
    const unique_date_time = record?.unique_date_time || null;
    const process = record?.process;

    const updateLink = `/production/post-cutting-process/update`;

    // Create an object with all the potential query parameters
    const allQueryParams = {
      sales_contract_id,
      style_id,
      sub_contract_factory_id,
      buying_office_id,
      movement_date,
      unique_date_time,
      process,
    };

    // Filter out null or undefined values
    const queryParams = Object.fromEntries(
        Object.entries(allQueryParams).filter(
            ([_, value]) => value !== null && value !== undefined
        )
    );

    navigate({
      pathname: updateLink,
      search: new URLSearchParams(queryParams).toString(),
    });
  };

  const tableColumns = [
    {
      title: "Buyer",
      dataIndex: ["buyer_name"],
      key: "buyer_name",
    },
    {
      title: "Style",
      dataIndex: ["styles", "style_no"],
      key: "style_no",
    },
    {
      title: "Movement Date",
      dataIndex: ["movement_date"],
      key: "movement_date",
      render: (value) => cglFormatDate(value)
    },
    {
      title: "Process",
      dataIndex: ["process"],
      key: "process",
    },
    {
      title: "Send Qty",
      dataIndex: ["send_qty"],
      key: "send_qty",
    },
    {
      title: "Receive Qty",
      dataIndex: ["receive_qty"],
      key: "receive_qty",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Button
            size="small"
            onClick={() => gotoUpdateRoute(record)}
            icon={<ExportOutlined />}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const tableTitleContent = (<div className={style?.titleHeader}>
    <div className={style?.totalContainer}>
      <div className={style.totalItem}><span className={style.totalLabel}>Total Receive: </span> {metaData?.summaryReceive} </div>
      <div className={style.totalItem}><span className={style.totalLabel}>Total Send: </span> {metaData?.summarySend} </div>
    </div>
  </div>)

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllPostCuttingProcess}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="post_cutting_filter"
      />
    ),
    [showFilter],
  );


  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={gotoAddRoute}
                disabled={!hasPermission([CUTTING_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([CUTTING_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title="Embroidery, Printing, Smocking and Pintuck"
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => refetch()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                title={()=>tableTitleContent}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
                sticky={true}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default PostCuttingProcess;
